import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { aboutCopy } from '../data/translations/about';
import { LIGHT_PHOTO_FILTER } from '../utils/photoFilters';
import { useScreenInit } from '../useScreenInit.js';
export function AboutSection() {
  const screenInit = useScreenInit();
  const [isExpanded, setIsExpanded] = useState<boolean>(screenInit?.aboutExpanded ?? false);
  const {
    language
  } = useLanguage();
  const copy = aboutCopy[language];
  return <section id="about" className="surface-a blend-top relative py-32 overflow-hidden" data-id="element-4" data-mp="1xnngocrt8d7j">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-5" data-mp="1sou2sj8m2k6u">
        <div className="grid md:grid-cols-2 gap-12 items-start" data-id="element-6" data-mp="1noxz6l3tsurt">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-7" data-mp="1nq94ipcx6al1">
            <div className="mb-8" data-id="element-8" data-mp="xiswslqi113n">
              <h2 className="font-serif text-4xl md:text-5xl text-ink-900" data-id="element-9" data-mp="xj2smpy862h9">
                {copy.heading}
              </h2>
            </div>

            <div className="space-y-6 text-ink-600 text-lg leading-relaxed" data-id="element-10" data-mp="irttnq8fn4rg">
              {copy.teaser.map((paragraph, index) => <p key={index} data-id="element-11" data-mp="1bi2drnmj3z8o" data-mpi={index}>{paragraph}</p>)}

              <AnimatePresence data-id="element-12" data-mp="1bghh9sgnm686">
                {isExpanded && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.3,
                ease: [0.23, 1, 0.32, 1]
              }} className="overflow-hidden space-y-6" data-id="element-13" data-mp="1ggcaw3rtd9wx">
                    {copy.expanded.map((paragraph, index) => <p key={index} data-id="element-14" data-mp="3rb1ehiuzmzil" data-mpi={index}>{paragraph}</p>)}
                  </motion.div>}
              </AnimatePresence>

              <button onClick={() => setIsExpanded(!isExpanded)} aria-expanded={isExpanded} className="inline-flex items-center gap-2 text-bronze-600 hover:text-ink-900 font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-bronze-400 rounded group" data-id="element-15" data-mp="1srd3fmh5cc23">
                <span data-id="element-16" data-mp="1f1t0afjnlcs9">{isExpanded ? copy.readLess : copy.readMore}</span>
                {isExpanded ? <ChevronUp size={20} className="group-hover:-translate-y-0.5 transition-transform" data-id="element-17" data-mp="1a3hucfwhbmfb" /> : <ChevronDown size={20} className="group-hover:translate-y-0.5 transition-transform" data-id="element-18" data-mp="10520i562hz24" />}
              </button>

              {/* Closing quote-like statement */}
              <div className="pt-4 border-l-2 border-bronze-400 pl-5" data-id="element-19" data-mp="32yskdydsaxzx">
                <p className="font-serif italic text-xl md:text-2xl text-ink-900 leading-relaxed" data-id="element-20" data-mp="32wg03ekk0bjp">
                  {copy.quote}
                </p>
              </div>
            </div>

            {/* ICF Badge */}
            <div className="mt-8 space-y-3" data-id="element-21" data-mp="3gpe2vlzbt5xa">
              <div className="inline-flex items-center space-x-3 text-bronze-600 bg-[rgba(255,255,255,0.55)] p-4 rounded-lg border border-cardline" data-id="element-22" data-mp="3gp5qdv7482mm">
                <CheckCircle2 size={20} data-id="element-23" data-mp="2o0qbd8nzhkba" />
                <span className="font-medium tracking-wide text-sm" data-id="element-24" data-mp="2o1rpxn47koxr">
                  {copy.icfBadge}
                </span>
              </div>
              <p className="text-ink-600 text-sm leading-relaxed max-w-lg" data-id="element-25" data-mp="2xz641g2nt1mw">
                {copy.icfPrefix}
                <a href="https://coachingfederation.org/resource/icf-core-competencies/" target="_blank" rel="noopener noreferrer" className="text-bronze-600 hover:text-ink-900 underline underline-offset-2 transition-colors" data-id="element-26" data-mp="8a44anunaiu9">
                  {copy.icfLinkText}
                </a>
                {copy.icfSuffix}
              </p>
            </div>
          </motion.div>

          {/* Right side - Mark's portrait */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="hidden md:flex items-start justify-center" data-id="element-27" data-mp="1q6z43of1nl60">
            <div className="relative" data-id="element-28" data-mp="2dxl1mlrf9n1r">
              <div className="absolute -inset-3 bg-bronze-400/15 rounded-2xl transform rotate-2" data-id="element-29" data-mp="2dwjpedyo6uig"></div>
              <img src="https://cdn.magicpatterns.com/uploads/636eGguFiQZXSCeJScNMcQ/20260920-Mark-LR35.jpg" alt={copy.portraitAlt} style={{
              filter: LIGHT_PHOTO_FILTER
            }} className="relative rounded-2xl shadow-xl w-full max-w-md object-cover border border-line" data-id="element-30" data-mp="29vzs9ae3jqwb" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}